// frontend/src/components/DragDropZone/DragDropZone.js
// Purpose: Allow drag-and-drop file upload

// Desired outcome: Create an intuitive drag-and-drop interface for file uploads
// with progress indication and file type validation

import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { uploadFile } from '../../state/analysis/analysisSlice';
import logger from '../../utils/logger';
import './DragDropZone.css';

const DragDropZone = ({ onUploadSuccess, onUploadError }) => {
  const dispatch = useDispatch();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  // Allowed file types
  const allowedTypes = ['.xlsx', '.xls', '.csv', '.json'];
  
  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };
  
  // Process file upload
  const processFile = (file) => {
    // Validate file type
    const fileExt = `.${file.name.split('.').pop().toLowerCase()}`;
    if (!allowedTypes.includes(fileExt)) {
      onUploadError(`Invalid file type. Allowed types: ${allowedTypes.join(', ')}`);
      return;
    }
    
    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      onUploadError('File too large. Maximum size is 10MB.');
      return;
    }
    
    // Upload file
    setIsUploading(true);
    setUploadProgress(0);
    
    // Create simulated progress updates
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        const newProgress = prev + 5;
        if (newProgress >= 90) {
          clearInterval(progressInterval);
          return 90; // Hold at 90% until the actual upload completes
        }
        return newProgress;
      });
    }, 200);
    
    dispatch(uploadFile(file))
      .unwrap()
      .then(() => {
        clearInterval(progressInterval);
        setUploadProgress(100);
        setIsUploading(false);
        onUploadSuccess();
        logger.info('File uploaded successfully');
        
        // Reset progress after a delay
        setTimeout(() => {
          setUploadProgress(0);
        }, 1000);
      })
      .catch(err => {
        clearInterval(progressInterval);
        setIsUploading(false);
        setUploadProgress(0);
        onUploadError(err.message || 'Error uploading file');
        logger.error('Upload error', err);
      });
  };
  
  // Handle dropped files
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0]; // Only process the first file
      processFile(file);
    }
  };
  
  // Handle file input change
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      processFile(file);
    }
  };
  
  // Trigger file input click
  const openFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="drag-drop-container">
      <div 
        className={`drag-drop-zone ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFileSelector}
      >
        {isUploading ? (
          <div className="upload-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p>Uploading: {uploadProgress}%</p>
          </div>
        ) : (
          <>
            <div className="upload-icon">
              <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
            </div>
            <p>Drag & drop a file here, or click to select</p>
            <p className="file-types">Allowed file types: {allowedTypes.join(', ')}</p>
          </>
        )}
      </div>
      
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        accept={allowedTypes.join(',')}
      />
    </div>
  );
};

export default DragDropZone;